import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
} from '@angular/material/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AuthenticationService } from '@flexyoo/auth';
import { LOGGER_LEVEL } from '@flexyoo/core';
import { provideEmployeeStore } from '@flexyoo/employee/store';
import { environment } from '@flexyoo/environment';
import { provideMbpOwnershipCostStore } from '@flexyoo/mbp/store';
import {
  FILTERS_SAVER_TOKEN,
  LangService,
  UserNotificationsService,
} from '@flexyoo/shared/services';
import { LoaderComponent } from '@flexyoo/shared/ui';
import { TenantBenefitsService } from '@flexyoo/tenant-benefits/data-access';
import { provideTenantGroupSettingsStore } from '@flexyoo/tenant-group/store';
import {
  provideTenantDictionariesStore,
  provideTenantSettingsStore,
  provideTenantYearClosingStore,
} from '@flexyoo/tenant/store';
import { provideUserNotificationsStore, provideUserSettingsStore } from '@flexyoo/user/store';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import 'moment/locale/en-gb';
import 'moment/locale/fr';
import 'moment/locale/nl';
import { HrFiltersSaverService } from 'src/app/hr/shared/services/hr-filters-saver.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { loginRequest, msalConfig } from './auth-config';
import { AdminGuard } from './authentication/services/admin-guard.service';
import { CafeteriaUserAgreementGuard } from './authentication/services/cafeteria-user-agreement-guard.service';
import { ChoosePasswordGuard } from './authentication/services/choose-password-guard.service';
import { EmployeeAccountGuard } from './authentication/services/employee-account-guard.service';
import { EmployeeGuard } from './authentication/services/employee-guard.service';
import { EmployeeVandenBorreOrderGuardService } from './authentication/services/employee-vanden-borre-order-guard.service';
import { ForgotPasswordGuard } from './authentication/services/forgot-password-guard.service';
import { AuthGuard } from './authentication/services/guard.service';
import { HRGuard } from './authentication/services/hr-guard.service';
import { LoginGuard } from './authentication/services/login-guard.service';
import { OpcoGuard } from './authentication/services/opco-guard.service';
import { UserAccountGuard } from './authentication/services/user-account-guard.service';
import { UserAgreementGuard } from './authentication/services/user-agreement-guard.service';
import { HrEmployeeListFiltersService } from './hr/hr-employee-list/hr-employee-list-filters.service';
import { NotificationsSidebarService } from './hr/hr-notifications-dropdown/services/notifications-sidebar.service';
import { OidcAuthConfigModule } from './oidc-auth-config';
import { GlobaltInterceptorService } from './shared/services/http.interceptor';
import { TrimInterceptorService } from './shared/services/trim.interceptor';

registerLocaleData(localeDe);

export const MY_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MsalGuardConfigurationFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?cb=' + new Date().getTime());
}

@NgModule({
  declarations: [AppComponent],
  providers: [
    HrEmployeeListFiltersService,
    UserNotificationsService,
    NotificationsSidebarService,
    { provide: FILTERS_SAVER_TOKEN, useClass: HrFiltersSaverService },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    {
      provide: LOGGER_LEVEL,
      useValue: Boolean(environment.loggerLevel),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobaltInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TrimInterceptorService,
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'nl-BE' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigurationFactory,
    },
    TenantBenefitsService,
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    AuthGuard,
    UserAgreementGuard,
    CafeteriaUserAgreementGuard,
    LoginGuard,
    AdminGuard,
    HRGuard,
    OpcoGuard,
    EmployeeGuard,
    EmployeeVandenBorreOrderGuardService,
    UserAccountGuard,
    EmployeeAccountGuard,
    ChoosePasswordGuard,
    ForgotPasswordGuard,
    AuthenticationService,
    provideHttpClient(withInterceptorsFromDi()),
    provideStore(),
    provideEffects(),
    provideStoreDevtools(),
    provideTenantSettingsStore(),
    provideTenantGroupSettingsStore(),
    provideTenantYearClosingStore(),
    provideTenantDictionariesStore(),
    provideUserSettingsStore(),
    provideUserNotificationsStore(),
    provideEmployeeStore(),
    provideMbpOwnershipCostStore(),
  ],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,

    OidcAuthConfigModule,
    LoaderComponent,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(langService: LangService) {
    langService.initLang();
  }
}
